// src/WebMidiAdapter.ts
function isSecureContext() {
  return typeof window !== "undefined" && window.isSecureContext === true;
}
function isWebMidiSupported() {
  return typeof navigator !== "undefined" && typeof navigator.requestMIDIAccess === "function";
}
function getBrowserCompatibility() {
  const ua = navigator.userAgent;
  if (!isSecureContext()) {
    let browser = "your browser";
    if (ua.includes("Chrome") || ua.includes("Chromium")) browser = "Chrome";
    else if (ua.includes("Edg")) browser = "Edge";
    else if (ua.includes("Opera")) browser = "Opera";
    else if (ua.includes("Firefox")) browser = "Firefox";
    else if (ua.includes("Safari") && !ua.includes("Chrome")) browser = "Safari";
    return {
      supported: false,
      browser,
      notes: "Web MIDI requires a secure context (HTTPS or localhost)",
      requiresSecureContext: true
    };
  }
  if (ua.includes("Chrome") || ua.includes("Chromium")) {
    return { supported: true, browser: "Chrome", notes: "Full support with SysEx" };
  }
  if (ua.includes("Edg")) {
    return { supported: true, browser: "Edge", notes: "Full support with SysEx" };
  }
  if (ua.includes("Opera")) {
    return { supported: true, browser: "Opera", notes: "Full support with SysEx" };
  }
  if (ua.includes("Firefox")) {
    return {
      supported: false,
      browser: "Firefox",
      notes: "Web MIDI requires about:config flag (dom.webmidi.enabled)"
    };
  }
  if (ua.includes("Safari") && !ua.includes("Chrome")) {
    return { supported: false, browser: "Safari", notes: "Web MIDI not supported" };
  }
  return { supported: isWebMidiSupported(), browser: "Unknown", notes: "" };
}
async function requestMidiAccess() {
  if (!isWebMidiSupported()) {
    throw new Error("Web MIDI API not available. Please use Chrome, Edge, or Opera.");
  }
  const access = await navigator.requestMIDIAccess({ sysex: true });
  const inputs = [];
  const outputs = [];
  access.inputs.forEach((port) => {
    const midiPort = {
      id: port.id,
      name: port.name ?? `Input ${port.id}`,
      state: port.state
    };
    if (port.manufacturer) {
      midiPort.manufacturer = port.manufacturer;
    }
    inputs.push(midiPort);
  });
  access.outputs.forEach((port) => {
    const midiPort = {
      id: port.id,
      name: port.name ?? `Output ${port.id}`,
      state: port.state
    };
    if (port.manufacturer) {
      midiPort.manufacturer = port.manufacturer;
    }
    outputs.push(midiPort);
  });
  return {
    inputs,
    outputs,
    sysExEnabled: access.sysexEnabled
  };
}
function createWebMidiAdapter(input, output) {
  const listeners = /* @__PURE__ */ new Map();
  let sysExBuffer = [];
  let isReceivingSysEx = false;
  return {
    send(message) {
      output.send(new Uint8Array(message));
    },
    onSysEx(callback) {
      const listener = (e) => {
        if (!e.data || e.data.length === 0) return;
        const data = Array.from(e.data);
        const firstByte = data[0];
        const lastByte = data[data.length - 1];
        if (firstByte === 240 && lastByte === 247) {
          callback(data);
          return;
        }
        if (firstByte === 240 && lastByte !== 247) {
          sysExBuffer = data;
          isReceivingSysEx = true;
          return;
        }
        if (isReceivingSysEx) {
          sysExBuffer.push(...data);
          if (lastByte === 247) {
            callback(sysExBuffer);
            sysExBuffer = [];
            isReceivingSysEx = false;
          }
          return;
        }
      };
      listeners.set(callback, listener);
      input.addEventListener("midimessage", listener);
    },
    removeSysExListener(callback) {
      const listener = listeners.get(callback);
      if (listener) {
        input.removeEventListener("midimessage", listener);
        listeners.delete(callback);
      }
    }
  };
}
async function openMidiPorts(inputId, outputId) {
  if (!isWebMidiSupported()) {
    throw new Error("Web MIDI API not available");
  }
  const access = await navigator.requestMIDIAccess({ sysex: true });
  let input;
  let output;
  access.inputs.forEach((port) => {
    if (port.id === inputId) {
      input = port;
    }
  });
  access.outputs.forEach((port) => {
    if (port.id === outputId) {
      output = port;
    }
  });
  if (!input) {
    throw new Error(`MIDI input port not found: ${inputId}`);
  }
  if (!output) {
    throw new Error(`MIDI output port not found: ${outputId}`);
  }
  const selectedInput = input;
  const selectedOutput = output;
  await selectedInput.open();
  await selectedOutput.open();
  const adapter = createWebMidiAdapter(selectedInput, selectedOutput);
  const cleanup = async () => {
    await selectedInput.close();
    await selectedOutput.close();
  };
  return { adapter, cleanup };
}

// src/retry.ts
function calculateDelay(attempt, initialDelayMs, maxDelayMs, multiplier, jitter) {
  const exponentialDelay = initialDelayMs * Math.pow(multiplier, attempt);
  const cappedDelay = Math.min(exponentialDelay, maxDelayMs);
  const jitterRange = cappedDelay * jitter;
  const jitteredDelay = cappedDelay - jitterRange + Math.random() * jitterRange;
  return Math.floor(jitteredDelay);
}
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
function withTimeout(promise, timeoutMs) {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error(`Operation timed out after ${timeoutMs}ms`));
    }, timeoutMs);
    promise.then((value) => {
      clearTimeout(timeoutId);
      resolve(value);
    }).catch((error) => {
      clearTimeout(timeoutId);
      reject(error);
    });
  });
}
async function withRetry(operation, options = {}) {
  const {
    maxRetries = 3,
    initialDelayMs = 100,
    maxDelayMs = 1e4,
    multiplier = 2,
    jitter = 0.5,
    isRetryable = () => true,
    onRetry,
    attemptTimeoutMs
  } = options;
  let lastError;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = attemptTimeoutMs ? await withTimeout(operation(), attemptTimeoutMs) : await operation();
      return result;
    } catch (error) {
      lastError = error;
      if (attempt >= maxRetries) {
        break;
      }
      if (!isRetryable(error)) {
        break;
      }
      const delayMs = calculateDelay(
        attempt,
        initialDelayMs,
        maxDelayMs,
        multiplier,
        jitter
      );
      onRetry?.(attempt + 1, error, delayMs);
      await sleep(delayMs);
    }
  }
  throw lastError;
}
async function withRetryResult(operation, options = {}) {
  const startTime = Date.now();
  let attempts = 0;
  const trackingOptions = {
    ...options,
    onRetry: (attempt, error, delayMs) => {
      attempts = attempt;
      options.onRetry?.(attempt, error, delayMs);
    }
  };
  try {
    const value = await withRetry(operation, trackingOptions);
    return {
      success: true,
      value,
      attempts: attempts + 1,
      elapsedMs: Date.now() - startTime
    };
  } catch (error) {
    return {
      success: false,
      error,
      attempts: (options.maxRetries ?? 3) + 1,
      elapsedMs: Date.now() - startTime
    };
  }
}
function createRetryWrapper(defaultOptions) {
  return function(operation, overrideOptions = {}) {
    return withRetry(operation, { ...defaultOptions, ...overrideOptions });
  };
}

// src/sds/sds-constants.ts
var SYSEX_START = 240;
var SYSEX_END = 247;
var UNIVERSAL_NON_REAL_TIME = 126;
var CMD_DUMP_HEADER = 1;
var CMD_DATA_PACKET = 2;
var CMD_DUMP_REQUEST = 3;
var CMD_ACK = 127;
var CMD_NAK = 126;
var CMD_WAIT = 124;
var CMD_CANCEL = 125;
var LOOP_FORWARD = 0;
var LOOP_FORWARD_BACKWARD = 1;
var LOOP_OFF = 127;
var DATA_PACKET_PAYLOAD_SIZE = 120;
var PACKET_COUNTER_MAX = 128;
var MAX_SAMPLE_NUMBER = 16383;
var MIN_BIT_DEPTH = 8;
var MAX_BIT_DEPTH = 28;
var DEFAULT_TIMEOUT_MS = 2e3;
var DEFAULT_MAX_RETRIES = 3;

// src/sds/sds-messages.ts
var MAX_21_BIT = 2097151;
function encode7bit14(value) {
  return [value & 127, value >> 7 & 127];
}
function encode7bit21(value) {
  return [value & 127, value >> 7 & 127, value >> 14 & 127];
}
function decode7bit14(lsb, msb) {
  return (msb & 127) << 7 | lsb & 127;
}
function decode7bit21(low, mid, high) {
  return (high & 127) << 14 | (mid & 127) << 7 | low & 127;
}
function validateChannel(channel) {
  if (!Number.isInteger(channel) || channel < 0 || channel > 127) {
    throw new Error(
      `SDS channel must be an integer 0-127, got ${channel}`
    );
  }
}
function validateSampleNumber(sampleNumber) {
  if (!Number.isInteger(sampleNumber) || sampleNumber < 0 || sampleNumber > MAX_SAMPLE_NUMBER) {
    throw new Error(
      `SDS sample number must be an integer 0-${MAX_SAMPLE_NUMBER}, got ${sampleNumber}`
    );
  }
}
function validatePacketNumber(packetNumber) {
  if (!Number.isInteger(packetNumber) || packetNumber < 0 || packetNumber >= PACKET_COUNTER_MAX) {
    throw new Error(
      `SDS packet number must be an integer 0-${PACKET_COUNTER_MAX - 1}, got ${packetNumber}`
    );
  }
}
function validate21BitValue(value, name) {
  if (!Number.isInteger(value) || value < 0 || value > MAX_21_BIT) {
    throw new Error(
      `SDS ${name} must be an integer 0-${MAX_21_BIT}, got ${value}`
    );
  }
}
function calculateChecksum(data) {
  let xor = 0;
  for (const byte of data) {
    xor ^= byte;
  }
  return xor & 127;
}
function validateChecksum(packet, channel) {
  validateChannel(channel);
  const checksumRegion = [
    UNIVERSAL_NON_REAL_TIME,
    channel,
    CMD_DATA_PACKET,
    packet.packetNumber & 127,
    ...Array.from(packet.data)
  ];
  return calculateChecksum(checksumRegion) === packet.checksum;
}
function buildDumpHeader(channel, header) {
  validateChannel(channel);
  validateSampleNumber(header.sampleNumber);
  if (!Number.isInteger(header.sampleFormat) || header.sampleFormat < MIN_BIT_DEPTH || header.sampleFormat > MAX_BIT_DEPTH) {
    throw new Error(
      `SDS sample format (bits) must be ${MIN_BIT_DEPTH}-${MAX_BIT_DEPTH}, got ${header.sampleFormat}`
    );
  }
  validate21BitValue(header.samplePeriodNs, "sample period");
  validate21BitValue(header.sampleLength, "sample length");
  validate21BitValue(header.loopStart, "loop start");
  validate21BitValue(header.loopEnd, "loop end");
  const [sl, sh] = encode7bit14(header.sampleNumber);
  const [pl, pm, ph] = encode7bit21(header.samplePeriodNs);
  const [al, am, ah] = encode7bit21(header.sampleLength);
  const [bl, bm, bh] = encode7bit21(header.loopStart);
  const [cl, cm, ch] = encode7bit21(header.loopEnd);
  return [
    SYSEX_START,
    UNIVERSAL_NON_REAL_TIME,
    channel,
    CMD_DUMP_HEADER,
    sl,
    sh,
    header.sampleFormat,
    pl,
    pm,
    ph,
    al,
    am,
    ah,
    bl,
    bm,
    bh,
    cl,
    cm,
    ch,
    header.loopType,
    SYSEX_END
  ];
}
function buildDataPacket(channel, packetNumber, data) {
  validateChannel(channel);
  validatePacketNumber(packetNumber);
  if (data.length > DATA_PACKET_PAYLOAD_SIZE) {
    throw new Error(
      `SDS data packet payload must be at most ${DATA_PACKET_PAYLOAD_SIZE} bytes, got ${data.length}`
    );
  }
  const padded = new Uint8Array(DATA_PACKET_PAYLOAD_SIZE);
  padded.set(data);
  const kk = packetNumber & 127;
  const checksumRegion = [
    UNIVERSAL_NON_REAL_TIME,
    channel,
    CMD_DATA_PACKET,
    kk,
    ...Array.from(padded)
  ];
  const cs = calculateChecksum(checksumRegion);
  return [
    SYSEX_START,
    UNIVERSAL_NON_REAL_TIME,
    channel,
    CMD_DATA_PACKET,
    kk,
    ...Array.from(padded),
    cs,
    SYSEX_END
  ];
}
function buildDumpRequest(channel, sampleNumber) {
  validateChannel(channel);
  validateSampleNumber(sampleNumber);
  const [sl, sh] = encode7bit14(sampleNumber);
  return [
    SYSEX_START,
    UNIVERSAL_NON_REAL_TIME,
    channel,
    CMD_DUMP_REQUEST,
    sl,
    sh,
    SYSEX_END
  ];
}
function buildAck(channel, packetNumber) {
  validateChannel(channel);
  validatePacketNumber(packetNumber);
  return [
    SYSEX_START,
    UNIVERSAL_NON_REAL_TIME,
    channel,
    CMD_ACK,
    packetNumber & 127,
    SYSEX_END
  ];
}
function buildNak(channel, packetNumber) {
  validateChannel(channel);
  validatePacketNumber(packetNumber);
  return [
    SYSEX_START,
    UNIVERSAL_NON_REAL_TIME,
    channel,
    CMD_NAK,
    packetNumber & 127,
    SYSEX_END
  ];
}
function buildWait(channel, packetNumber) {
  validateChannel(channel);
  validatePacketNumber(packetNumber);
  return [
    SYSEX_START,
    UNIVERSAL_NON_REAL_TIME,
    channel,
    CMD_WAIT,
    packetNumber & 127,
    SYSEX_END
  ];
}
function buildCancel(channel, packetNumber) {
  validateChannel(channel);
  validatePacketNumber(packetNumber);
  return [
    SYSEX_START,
    UNIVERSAL_NON_REAL_TIME,
    channel,
    CMD_CANCEL,
    packetNumber & 127,
    SYSEX_END
  ];
}
function parseSdsMessage(data) {
  if (data.length < 5) {
    throw new Error(
      `SDS message too short: expected at least 5 bytes, got ${data.length}`
    );
  }
  if (data[0] !== SYSEX_START) {
    throw new Error(
      `SDS message must start with 0xF0, got 0x${data[0].toString(16).padStart(2, "0")}`
    );
  }
  if (data[data.length - 1] !== SYSEX_END) {
    throw new Error(
      `SDS message must end with 0xF7, got 0x${data[data.length - 1].toString(16).padStart(2, "0")}`
    );
  }
  if (data[1] !== UNIVERSAL_NON_REAL_TIME) {
    throw new Error(
      `SDS message byte[1] must be 0x7E (Universal Non-Real Time), got 0x${data[1].toString(16).padStart(2, "0")}`
    );
  }
  const channel = data[2];
  const command = data[3];
  switch (command) {
    case CMD_DUMP_HEADER:
      return parseDumpHeader(data, channel);
    case CMD_DATA_PACKET:
      return parseDataPacket(data, channel);
    case CMD_DUMP_REQUEST:
      return parseDumpRequestMsg(data, channel);
    case CMD_ACK:
      return parseHandshake(data, channel, "ack");
    case CMD_NAK:
      return parseHandshake(data, channel, "nak");
    case CMD_WAIT:
      return parseHandshake(data, channel, "wait");
    case CMD_CANCEL:
      return parseHandshake(data, channel, "cancel");
    default:
      throw new Error(
        `Unknown SDS command byte: 0x${command.toString(16).padStart(2, "0")}`
      );
  }
}
function parseDumpHeader(data, channel) {
  const expectedLength = 21;
  if (data.length !== expectedLength) {
    throw new Error(
      `SDS Dump Header must be ${expectedLength} bytes, got ${data.length}`
    );
  }
  const sampleNumber = decode7bit14(data[4], data[5]);
  const sampleFormat = data[6];
  const samplePeriodNs = decode7bit21(data[7], data[8], data[9]);
  const sampleLength = decode7bit21(data[10], data[11], data[12]);
  const loopStart = decode7bit21(data[13], data[14], data[15]);
  const loopEnd = decode7bit21(data[16], data[17], data[18]);
  const loopType = data[19];
  return {
    type: "dump-header",
    channel,
    header: {
      sampleNumber,
      sampleFormat,
      samplePeriodNs,
      sampleLength,
      loopStart,
      loopEnd,
      loopType
    }
  };
}
function parseDataPacket(data, channel) {
  const expectedLength = 5 + DATA_PACKET_PAYLOAD_SIZE + 2;
  if (data.length !== expectedLength) {
    throw new Error(
      `SDS Data Packet must be ${expectedLength} bytes, got ${data.length}`
    );
  }
  const packetNumber = data[4] & 127;
  const payloadStart = 5;
  const payloadEnd = payloadStart + DATA_PACKET_PAYLOAD_SIZE;
  const packetData = new Uint8Array(
    data.slice(payloadStart, payloadEnd)
  );
  const checksum = data[payloadEnd];
  return {
    type: "data-packet",
    channel,
    packet: {
      packetNumber,
      data: packetData,
      checksum
    }
  };
}
function parseDumpRequestMsg(data, channel) {
  const expectedLength = 7;
  if (data.length !== expectedLength) {
    throw new Error(
      `SDS Dump Request must be ${expectedLength} bytes, got ${data.length}`
    );
  }
  const sampleNumber = decode7bit14(data[4], data[5]);
  return {
    type: "dump-request",
    channel,
    request: { sampleNumber }
  };
}
function parseHandshake(data, channel, kind) {
  const expectedLength = 6;
  if (data.length !== expectedLength) {
    throw new Error(
      `SDS ${kind.toUpperCase()} must be ${expectedLength} bytes, got ${data.length}`
    );
  }
  const packetNumber = data[4] & 127;
  return { type: kind, channel, packetNumber };
}

// src/sds/sds-encoding.ts
function validateBitsPerWord(bitsPerWord) {
  if (!Number.isInteger(bitsPerWord) || bitsPerWord < MIN_BIT_DEPTH || bitsPerWord > MAX_BIT_DEPTH) {
    throw new Error(
      `bitsPerWord must be an integer between ${MIN_BIT_DEPTH} and ${MAX_BIT_DEPTH}, got ${bitsPerWord}`
    );
  }
}
function validateMidiByte(byte, index) {
  if (byte < 0 || byte > 127) {
    throw new Error(
      `MIDI byte at index ${index} must be 0-127, got ${byte}`
    );
  }
}
function bytesPerWord(bitsPerWord) {
  return Math.ceil(bitsPerWord / 7);
}
function encodeSampleWord(value, bitsPerWord) {
  validateBitsPerWord(bitsPerWord);
  const numBytes = bytesPerWord(bitsPerWord);
  const result = new Array(numBytes);
  for (let i = 0; i < numBytes; i++) {
    const bitsFromTop = 7 * (i + 1);
    const shift = bitsPerWord - bitsFromTop;
    if (shift >= 0) {
      result[i] = value >>> shift & 127;
    } else {
      result[i] = value << -shift & 127;
    }
  }
  return result;
}
function decodeSampleWord(midiBytes, bitsPerWord) {
  validateBitsPerWord(bitsPerWord);
  const numBytes = bytesPerWord(bitsPerWord);
  if (midiBytes.length < numBytes) {
    throw new Error(
      `Expected at least ${numBytes} MIDI bytes for ${bitsPerWord}-bit sample, got ${midiBytes.length}`
    );
  }
  let assembled = 0;
  for (let i = 0; i < numBytes; i++) {
    validateMidiByte(midiBytes[i], i);
    const bitsFromTop = 7 * (i + 1);
    const shift = bitsPerWord - bitsFromTop;
    if (shift >= 0) {
      assembled |= (midiBytes[i] & 127) << shift;
    } else {
      assembled |= (midiBytes[i] & 127) >>> -shift;
    }
  }
  return assembled;
}
function encodeSampleData(samples, bitsPerWord) {
  validateBitsPerWord(bitsPerWord);
  const numBytes = bytesPerWord(bitsPerWord);
  const result = new Uint8Array(samples.length * numBytes);
  const maxUnsigned = (1 << bitsPerWord) - 1;
  for (let s = 0; s < samples.length; s++) {
    const value = samples[s] & maxUnsigned;
    const encoded = encodeSampleWord(value, bitsPerWord);
    const offset = s * numBytes;
    for (let b = 0; b < numBytes; b++) {
      result[offset + b] = encoded[b];
    }
  }
  return result;
}
function decodeSampleData(midiBytes, bitsPerWord, sampleCount) {
  validateBitsPerWord(bitsPerWord);
  const numBytes = bytesPerWord(bitsPerWord);
  const requiredBytes = sampleCount * numBytes;
  if (midiBytes.length < requiredBytes) {
    throw new Error(
      `Expected at least ${requiredBytes} MIDI bytes for ${sampleCount} samples at ${bitsPerWord} bits/word, got ${midiBytes.length}`
    );
  }
  const result = bitsPerWord <= 16 ? new Int16Array(sampleCount) : new Int32Array(sampleCount);
  for (let s = 0; s < sampleCount; s++) {
    const offset = s * numBytes;
    const chunk = Array.from(midiBytes.subarray(offset, offset + numBytes));
    result[s] = decodeSampleWord(chunk, bitsPerWord);
  }
  return result;
}
function calculatePacketCount(sampleCount, bitsPerWord) {
  validateBitsPerWord(bitsPerWord);
  if (sampleCount <= 0) {
    return 0;
  }
  const numBytes = bytesPerWord(bitsPerWord);
  const totalBytes = sampleCount * numBytes;
  return Math.ceil(totalBytes / DATA_PACKET_PAYLOAD_SIZE);
}
function samplesToPackets(samples, bitsPerWord) {
  validateBitsPerWord(bitsPerWord);
  const encoded = encodeSampleData(samples, bitsPerWord);
  const packetCount = calculatePacketCount(samples.length, bitsPerWord);
  const packets = [];
  for (let p = 0; p < packetCount; p++) {
    const start = p * DATA_PACKET_PAYLOAD_SIZE;
    const end = Math.min(start + DATA_PACKET_PAYLOAD_SIZE, encoded.length);
    const packet = new Uint8Array(DATA_PACKET_PAYLOAD_SIZE);
    packet.set(encoded.subarray(start, end));
    packets.push(packet);
  }
  return packets;
}
function packetsToSamples(packets, bitsPerWord, sampleCount) {
  validateBitsPerWord(bitsPerWord);
  if (packets.length === 0 && sampleCount > 0) {
    throw new Error(
      `Cannot decode ${sampleCount} samples from zero packets`
    );
  }
  const numBytes = bytesPerWord(bitsPerWord);
  const requiredBytes = sampleCount * numBytes;
  const totalAvailable = packets.length * DATA_PACKET_PAYLOAD_SIZE;
  if (totalAvailable < requiredBytes) {
    throw new Error(
      `Need at least ${requiredBytes} bytes for ${sampleCount} samples at ${bitsPerWord} bits/word, but ${packets.length} packets provide only ${totalAvailable} bytes`
    );
  }
  const flat = new Uint8Array(totalAvailable);
  for (let p = 0; p < packets.length; p++) {
    flat.set(packets[p], p * DATA_PACKET_PAYLOAD_SIZE);
  }
  return decodeSampleData(flat, bitsPerWord, sampleCount);
}

// src/sds/sds-transfer-util.ts
function makeProgress(packetIndex, totalPackets, bytesPerPacket, totalBytes) {
  const packetsSent = packetIndex + 1;
  return {
    packetsSent,
    packetsTotal: totalPackets,
    bytesSent: Math.min(packetsSent * bytesPerPacket, totalBytes),
    bytesTotal: totalBytes
  };
}
function tryParseSds(data) {
  try {
    return parseSdsMessage(data);
  } catch {
    return void 0;
  }
}

// src/sds/sds-receiver.ts
function createSdsReceiver(midiIn, options) {
  const { channel, onHeader, onProgress, onComplete, onError } = options;
  let active = false;
  let receivedHeader;
  let receivedPackets = [];
  let expectedPacketCount = 0;
  let packetsReceived = 0;
  function cleanup() {
    active = false;
    midiIn.removeSysExListener(listener);
  }
  function fail(error) {
    cleanup();
    if (onError) {
      onError(error);
    }
  }
  function listener(data) {
    if (!active) return;
    const msg = tryParseSds(data);
    if (!msg) return;
    if (msg.channel !== channel) return;
    if (!receivedHeader) {
      handleHeader(msg);
      return;
    }
    handleDataPacket(msg);
  }
  function handleHeader(msg) {
    if (msg.type !== "dump-header") return;
    receivedHeader = msg.header;
    expectedPacketCount = calculatePacketCount(
      msg.header.sampleLength,
      msg.header.sampleFormat
    );
    receivedPackets = new Array(expectedPacketCount);
    packetsReceived = 0;
    if (onHeader) {
      onHeader(msg.header);
    }
    midiIn.send(buildAck(channel, 0));
  }
  function handleDataPacket(msg) {
    if (msg.type !== "data-packet") return;
    const { packet } = msg;
    if (!validateChecksum(packet, channel)) {
      midiIn.send(buildNak(channel, packet.packetNumber));
      return;
    }
    const expectedCounter = packetsReceived % PACKET_COUNTER_MAX;
    if (packet.packetNumber !== expectedCounter) {
      midiIn.send(buildNak(channel, expectedCounter));
      return;
    }
    receivedPackets[packetsReceived] = packet.data;
    packetsReceived++;
    midiIn.send(buildAck(channel, packet.packetNumber));
    if (onProgress && receivedHeader) {
      const totalBytes = receivedHeader.sampleLength * Math.ceil(receivedHeader.sampleFormat / 7);
      onProgress({
        packetsSent: packetsReceived,
        packetsTotal: expectedPacketCount,
        bytesSent: Math.min(packetsReceived * 120, totalBytes),
        bytesTotal: totalBytes
      });
    }
    if (packetsReceived >= expectedPacketCount && receivedHeader) {
      finishTransfer();
    }
  }
  function finishTransfer() {
    if (!receivedHeader) {
      fail(new Error("Cannot finish transfer: no header received"));
      return;
    }
    cleanup();
    try {
      const samples = packetsToSamples(
        receivedPackets,
        receivedHeader.sampleFormat,
        receivedHeader.sampleLength
      );
      if (onComplete) {
        onComplete(samples);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error decoding sample data";
      if (onError) {
        onError(new Error(`Failed to decode received sample data: ${message}`));
      }
    }
  }
  function start() {
    if (active) {
      throw new Error("Receiver is already active");
    }
    active = true;
    receivedHeader = void 0;
    receivedPackets = [];
    expectedPacketCount = 0;
    packetsReceived = 0;
    midiIn.onSysEx(listener);
  }
  function cancel() {
    if (!active) return;
    const packetNumber = packetsReceived % PACKET_COUNTER_MAX;
    midiIn.send(buildCancel(channel, packetNumber));
    cleanup();
  }
  return { start, cancel };
}

// src/sds/sds-sender.ts
function createSdsSender(midiOut, options) {
  const {
    channel,
    header,
    samples,
    mode,
    onProgress,
    ackTimeoutMs = DEFAULT_TIMEOUT_MS,
    maxRetries = DEFAULT_MAX_RETRIES
  } = options;
  let cancelled = false;
  let rejectTransfer;
  function cancel() {
    cancelled = true;
    if (rejectTransfer) {
      rejectTransfer(new Error("Transfer cancelled by sender"));
    }
  }
  function start() {
    const packets = samplesToPackets(samples, header.sampleFormat);
    const totalPackets = packets.length;
    const totalBytes = samples.length * Math.ceil(header.sampleFormat / 7);
    console.log(`[sds-sender] start(): mode=${mode}, packets=${totalPackets}, samples=${samples.length}, format=${header.sampleFormat}`);
    if (mode === "open-loop") {
      return sendOpenLoop(packets, totalPackets, totalBytes);
    }
    return sendClosedLoop(packets, totalPackets, totalBytes);
  }
  function sendOpenLoop(packets, totalPackets, totalBytes) {
    return new Promise((resolve, reject) => {
      rejectTransfer = reject;
      midiOut.send(buildDumpHeader(channel, header));
      for (let i = 0; i < packets.length; i++) {
        if (cancelled) {
          reject(new Error("Transfer cancelled by sender"));
          return;
        }
        const packetNumber = i % PACKET_COUNTER_MAX;
        midiOut.send(buildDataPacket(channel, packetNumber, packets[i]));
        if (onProgress) {
          onProgress(makeProgress(i, totalPackets, 120, totalBytes));
        }
      }
      rejectTransfer = void 0;
      resolve();
    });
  }
  function sendClosedLoop(packets, totalPackets, totalBytes) {
    return new Promise((resolve, reject) => {
      rejectTransfer = reject;
      let currentPacket = 0;
      let retryCount = 0;
      let timeoutId;
      let waitingForHeaderAck = true;
      function cleanup() {
        midiOut.removeSysExListener(listener);
        if (timeoutId !== void 0) {
          clearTimeout(timeoutId);
          timeoutId = void 0;
        }
        rejectTransfer = void 0;
      }
      function fail(error) {
        console.log(`[sds-sender] FAIL: ${error.message}`);
        cleanup();
        reject(error);
      }
      function succeed() {
        console.log(`[sds-sender] SUCCEED: all ${totalPackets} packets sent`);
        cleanup();
        resolve();
      }
      function startTimeout() {
        if (timeoutId !== void 0) {
          clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
          timeoutId = void 0;
          handleTimeout();
        }, ackTimeoutMs);
      }
      function handleTimeout() {
        if (cancelled) {
          fail(new Error("Transfer cancelled by sender"));
          return;
        }
        retryCount++;
        if (retryCount > maxRetries) {
          fail(
            new Error(
              `Transfer failed: no response after ${maxRetries} retries (packet ${currentPacket})`
            )
          );
          return;
        }
        if (waitingForHeaderAck) {
          midiOut.send(buildDumpHeader(channel, header));
          startTimeout();
          return;
        }
        sendCurrentPacket();
      }
      function sendCurrentPacket() {
        const packetNumber = currentPacket % PACKET_COUNTER_MAX;
        midiOut.send(
          buildDataPacket(channel, packetNumber, packets[currentPacket])
        );
        startTimeout();
      }
      function listener(data) {
        const msg = tryParseSds(data);
        if (!msg) return;
        if (msg.channel !== channel) return;
        if (cancelled) {
          fail(new Error("Transfer cancelled by sender"));
          return;
        }
        if (waitingForHeaderAck) {
          handleHeaderResponse(msg);
          return;
        }
        handlePacketResponse(msg);
      }
      function handleHeaderResponse(msg) {
        switch (msg.type) {
          case "ack":
            waitingForHeaderAck = false;
            clearActiveTimeout();
            if (totalPackets === 0) {
              succeed();
              return;
            }
            retryCount = 0;
            sendCurrentPacket();
            break;
          case "nak":
            retryCount++;
            if (retryCount > maxRetries) {
              fail(
                new Error(
                  `Transfer failed: dump header NAKed after ${maxRetries} retries`
                )
              );
              return;
            }
            clearActiveTimeout();
            midiOut.send(buildDumpHeader(channel, header));
            startTimeout();
            break;
          case "cancel":
            fail(new Error("Transfer cancelled by receiver"));
            break;
          case "wait":
            clearActiveTimeout();
            startTimeout();
            break;
          default:
            break;
        }
      }
      function handlePacketResponse(msg) {
        switch (msg.type) {
          case "ack": {
            clearActiveTimeout();
            if (onProgress) {
              onProgress(
                makeProgress(currentPacket, totalPackets, 120, totalBytes)
              );
            }
            currentPacket++;
            retryCount = 0;
            if (currentPacket >= totalPackets) {
              succeed();
              return;
            }
            sendCurrentPacket();
            break;
          }
          case "nak": {
            clearActiveTimeout();
            retryCount++;
            if (retryCount > maxRetries) {
              fail(
                new Error(
                  `Transfer failed: packet ${currentPacket} NAKed after ${maxRetries} retries`
                )
              );
              return;
            }
            sendCurrentPacket();
            break;
          }
          case "wait": {
            clearActiveTimeout();
            startTimeout();
            break;
          }
          case "cancel":
            fail(new Error("Transfer cancelled by receiver"));
            break;
          default:
            break;
        }
      }
      function clearActiveTimeout() {
        if (timeoutId !== void 0) {
          clearTimeout(timeoutId);
          timeoutId = void 0;
        }
      }
      console.log(`[sds-sender] closed-loop: registering listener, sending header, starting timeout (${ackTimeoutMs}ms)`);
      midiOut.onSysEx(listener);
      const headerMsg = buildDumpHeader(channel, header);
      console.log(`[sds-sender] dump header: ${headerMsg.length} bytes`);
      midiOut.send(headerMsg);
      startTimeout();
    });
  }
  return { start, cancel };
}

// src/sds/sds-transfer.ts
function requestSample(midiOut, channel, sampleNumber, options) {
  midiOut.send(buildDumpRequest(channel, sampleNumber));
  const receiver = createSdsReceiver(midiOut, options);
  receiver.start();
  return { receiver };
}

// src/transports/scsi-midi-transport.ts
function stripTrailingSlash(url) {
  return url.endsWith("/") ? url.slice(0, -1) : url;
}
function wsUrlFromHttp(httpUrl, path) {
  const base = stripTrailingSlash(httpUrl);
  if (base.startsWith("/")) {
    const protocol = typeof window !== "undefined" && window.location.protocol === "https:" ? "wss:" : "ws:";
    const host = typeof window !== "undefined" ? window.location.host : "localhost";
    return `${protocol}//${host}${base}${path}`;
  }
  const wsBase = base.replace(/^http/, "ws");
  return `${wsBase}${path}`;
}
async function fetchJson(baseUrl, path, options) {
  const url = `${stripTrailingSlash(baseUrl)}${path}`;
  const res = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers
    }
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`SCSI bridge HTTP ${res.status}: ${text}`);
  }
  return res.json();
}
function createScsiMidiTransport(options) {
  const bridgeUrl = stripTrailingSlash(options.bridgeUrl);
  const listeners = /* @__PURE__ */ new Set();
  let ws = null;
  let sendQueue = Promise.resolve();
  function dispatchResponses(data) {
    let start = 0;
    for (let i = 0; i < data.length; i++) {
      if (data[i] === 247) {
        const msg = data.slice(start, i + 1);
        if (msg[0] === 240) {
          listeners.forEach((cb) => cb(msg));
        }
        start = i + 1;
      }
    }
  }
  function handleWsMessage(event) {
    try {
      const msg = JSON.parse(String(event.data));
      if (msg.type !== "sysex" || !Array.isArray(msg.data)) {
        return;
      }
      if (msg.data.length > 0 && msg.data[0] === 240) {
        listeners.forEach((cb) => cb(msg.data));
      }
    } catch (err) {
      console.error("[ScsiMidiTransport] Failed to parse WebSocket message:", err);
    }
  }
  const adapter = {
    send(message) {
      const msgType = message[0] === 240 ? `F0..${message.slice(1, 4).map((b) => b.toString(16)).join(" ")}` : "non-sysex";
      console.log(`[ScsiMidi] send queued: ${message.length} bytes (${msgType}), wsOpen=${ws?.readyState === WebSocket.OPEN}`);
      sendQueue = sendQueue.then(async () => {
        try {
          console.log(`[ScsiMidi] sending via HTTP POST to ${bridgeUrl}/sds/send`);
          const jsonBody = JSON.stringify({ message });
          const res = await fetch(`${bridgeUrl}/sds/send`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: jsonBody
          });
          if (!res.ok) {
            const errText = await res.text();
            console.error(`[ScsiMidi] HTTP ${res.status}: ${errText} (sent ${message.length} bytes, body ${jsonBody.length} chars)`);
            return;
          }
          const body = await res.json();
          console.log(`[ScsiMidi] HTTP response: ok=${body.ok}, response=${body.response?.length ?? 0} bytes`);
          if (body.response && body.response.length > 0) {
            dispatchResponses(body.response);
          }
        } catch (err) {
          console.error("[ScsiMidiTransport] Send error:", err);
        }
      });
    },
    onSysEx(callback) {
      console.log(`[ScsiMidi] onSysEx registered, listeners: ${listeners.size + 1}`);
      listeners.add(callback);
    },
    removeSysExListener(callback) {
      listeners.delete(callback);
    }
  };
  async function connect() {
    await fetchJson(bridgeUrl, "/health").catch((err) => {
      throw new Error(
        `Failed to connect to SCSI bridge at ${bridgeUrl}: ${err instanceof Error ? err.message : String(err)}`
      );
    });
    const status = await Promise.race([
      fetchJson(bridgeUrl, "/status"),
      new Promise(
        (resolve) => setTimeout(() => resolve({
          version: "unknown",
          scsi2piVersion: "unknown",
          boardId: 0,
          samplerReachable: true
          // Optimistic — scan found it
        }), 3e3)
      )
    ]);
    try {
      const wsUrl = wsUrlFromHttp(bridgeUrl, "/sds/stream");
      ws = new WebSocket(wsUrl);
      ws.addEventListener("message", handleWsMessage);
      ws.addEventListener("error", (err) => {
        console.warn("[ScsiMidiTransport] WebSocket error (incoming SysEx will use polling):", err);
      });
    } catch (err) {
      console.warn("[ScsiMidiTransport] WebSocket not available (mixed-content or network error):", err);
    }
    return status;
  }
  function disconnect() {
    if (ws) {
      ws.removeEventListener("message", handleWsMessage);
      ws.close();
      ws = null;
    }
    listeners.clear();
  }
  async function scanDevices() {
    return fetchJson(bridgeUrl, "/scsi/scan");
  }
  const waitForSendQueueIdle = () => sendQueue;
  const sdsChannel = createScsiSdsChannel(bridgeUrl, waitForSendQueueIdle);
  return { adapter, sdsChannel, connect, disconnect, scanDevices };
}
var DEFAULT_SCSI_TARGET_ID = 6;
function createScsiSdsChannel(bridgeUrl, waitForIdle) {
  return {
    async uploadSample(sampleNumber, channel, sampleRate, samples, onProgress) {
      await waitForIdle();
      console.log(`[SdsChannel] send queue idle, starting upload`);
      const wsUrl = wsUrlFromHttp(bridgeUrl, "/sds/stream");
      return new Promise((resolve, reject) => {
        const sdsWs = new WebSocket(wsUrl);
        const STALL_MS = 3e4;
        let stallTimer = setTimeout(onStall, STALL_MS);
        function resetStallTimer() {
          clearTimeout(stallTimer);
          stallTimer = setTimeout(onStall, STALL_MS);
        }
        function onStall() {
          sdsWs.close();
          reject(new Error("Sample upload stalled \u2014 no progress for 30s"));
        }
        const totalPackets = Math.ceil(samples.length / 120);
        sdsWs.addEventListener("open", () => {
          sdsWs.send(JSON.stringify({
            type: "sample-upload-fast",
            target_id: DEFAULT_SCSI_TARGET_ID,
            sample_number: sampleNumber,
            channel,
            sample_rate: sampleRate,
            samples: Array.from(samples)
          }));
        });
        sdsWs.addEventListener("message", (event) => {
          const msg = JSON.parse(String(event.data));
          switch (msg.type) {
            case "upload-progress":
              resetStallTimer();
              if (onProgress) {
                onProgress({
                  packetsSent: msg.transferred ?? 0,
                  packetsTotal: msg.total ?? totalPackets,
                  bytesSent: (msg.transferred ?? 0) * 2,
                  bytesTotal: samples.length * 2
                });
              }
              break;
            case "upload-complete":
              clearTimeout(stallTimer);
              sdsWs.close();
              resolve();
              break;
            case "sample-error":
              clearTimeout(stallTimer);
              sdsWs.close();
              reject(new Error(`SDS upload error: ${msg.error}`));
              break;
          }
        });
        sdsWs.addEventListener("error", (event) => {
          clearTimeout(stallTimer);
          reject(new Error("SDS upload failed: WebSocket connection lost"));
        });
      });
    },
    async downloadSample(sampleNumber, channel, onProgress, signal) {
      await waitForIdle();
      console.log(`[SdsChannel] send queue idle, starting download`);
      const wsUrl = wsUrlFromHttp(bridgeUrl, "/sds/stream");
      return new Promise((resolve, reject) => {
        const sdsWs = new WebSocket(wsUrl);
        if (signal) {
          signal.addEventListener("abort", () => {
            sdsWs.close();
            reject(new Error("SDS download cancelled"));
          }, { once: true });
        }
        const timeoutMs = 6e4;
        const timeout = setTimeout(() => {
          sdsWs.close();
          reject(new Error(`SDS download timed out after ${timeoutMs / 1e3}s`));
        }, timeoutMs);
        let header;
        const sampleChunks = [];
        let totalSamples = 0;
        sdsWs.addEventListener("open", () => {
          sdsWs.send(JSON.stringify({
            type: "sample-download",
            targetId: DEFAULT_SCSI_TARGET_ID,
            sampleNumber,
            channel
          }));
        });
        sdsWs.addEventListener("message", (event) => {
          const msg = JSON.parse(String(event.data));
          switch (msg.type) {
            case "sample-header":
              header = {
                sampleNumber,
                sampleFormat: msg.bitsPerSample ?? 16,
                samplePeriodNs: msg.sampleRate ? Math.round(1e9 / msg.sampleRate) : 0,
                sampleLength: msg.sampleCount ?? 0,
                loopStart: msg.loopStart ?? 0,
                loopEnd: msg.loopEnd ?? 0,
                loopType: msg.loopType ?? 127
              };
              break;
            case "sample-data":
              if (Array.isArray(msg.samples)) {
                sampleChunks.push(msg.samples);
              }
              totalSamples = msg.transferred ?? totalSamples;
              if (onProgress && header) {
                onProgress({
                  packetsSent: totalSamples,
                  packetsTotal: header.sampleLength,
                  bytesSent: totalSamples * 2,
                  bytesTotal: header.sampleLength * 2
                });
              }
              break;
            case "sample-complete": {
              clearTimeout(timeout);
              sdsWs.close();
              if (!header) {
                reject(new Error("SDS download completed without receiving a dump header"));
                return;
              }
              const finalCount = msg.totalSamples ?? totalSamples;
              const allSamples = new Int16Array(finalCount);
              let offset = 0;
              for (const chunk of sampleChunks) {
                for (const s of chunk) {
                  if (offset < allSamples.length) {
                    allSamples[offset++] = s;
                  }
                }
              }
              resolve({ header, samples: allSamples });
              break;
            }
            case "sample-error":
              clearTimeout(timeout);
              sdsWs.close();
              reject(new Error(`SDS download error: ${msg.error}`));
              break;
          }
        });
        sdsWs.addEventListener("error", (event) => {
          clearTimeout(timeout);
          reject(new Error("SDS download failed: WebSocket connection lost"));
        });
        sdsWs.addEventListener("close", () => {
          clearTimeout(timeout);
          if (!header) {
            reject(new Error("SDS download WebSocket closed before receiving header"));
          }
        });
      });
    }
  };
}

// src/transports/scsi-disk-client.ts
function stripTrailingSlash2(url) {
  return url.endsWith("/") ? url.slice(0, -1) : url;
}
function createScsiDiskClient(bridgeUrl) {
  const baseUrl = stripTrailingSlash2(bridgeUrl);
  async function fetchJson2(path, options) {
    const res = await fetch(`${baseUrl}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...options?.headers
      }
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`SCSI bridge HTTP ${res.status}: ${text}`);
    }
    return res.json();
  }
  return {
    async inquiry(targetId) {
      return fetchJson2(`/scsi/inquiry/${targetId}`);
    },
    async readCapacity(targetId) {
      return fetchJson2(`/scsi/capacity/${targetId}`);
    },
    async readBlocks(targetId, lba, count) {
      const resp = await fetchJson2(
        "/scsi/read",
        {
          method: "POST",
          body: JSON.stringify({ target_id: targetId, lba, count })
        }
      );
      if (resp.status !== 0) {
        throw new Error(`SCSI READ failed: status ${resp.status}`);
      }
      return new Uint8Array(resp.data_in);
    },
    async writeBlocks(targetId, lba, data) {
      const resp = await fetchJson2(
        "/scsi/write",
        {
          method: "POST",
          body: JSON.stringify({
            target_id: targetId,
            lba,
            data: Array.from(data)
          })
        }
      );
      if (resp.status !== 0) {
        throw new Error(`SCSI WRITE failed: status ${resp.status}`);
      }
    },
    downloadSample(targetId, sampleNumber, channel, callbacks) {
      return sampleTransfer(baseUrl, {
        type: "sample-download",
        target_id: targetId,
        sample_number: sampleNumber,
        channel
      }, callbacks);
    },
    uploadSample(targetId, sampleNumber, channel, sampleRate, samples, callbacks) {
      return sampleTransfer(baseUrl, {
        type: "sample-upload",
        target_id: targetId,
        sample_number: sampleNumber,
        channel,
        sample_rate: sampleRate,
        samples: Array.from(samples)
      }, callbacks);
    }
  };
}
function sampleTransfer(baseUrl, request, callbacks) {
  const wsUrl = baseUrl.replace(/^http/, "ws") + "/sds/stream";
  return new Promise((resolve, reject) => {
    const ws = new WebSocket(wsUrl);
    const timeoutMs = 6e5;
    const timeout = setTimeout(() => {
      ws.close();
      reject(new Error("sample transfer timed out"));
    }, timeoutMs);
    ws.addEventListener("open", () => {
      ws.send(JSON.stringify(request));
    });
    ws.addEventListener("message", (event) => {
      const msg = JSON.parse(String(event.data));
      switch (msg.type) {
        case "sample-header":
          if ("onHeader" in callbacks) {
            callbacks.onHeader({
              name: msg.name,
              sampleRate: msg.sampleRate,
              sampleCount: msg.sampleCount
            });
          }
          break;
        case "sample-data":
          if ("onData" in callbacks) {
            callbacks.onData(
              new Int16Array(msg.samples),
              { transferred: msg.transferred, total: msg.total }
            );
          }
          break;
        case "upload-progress":
          if ("onProgress" in callbacks) {
            callbacks.onProgress({
              transferred: msg.transferred,
              total: msg.total
            });
          }
          break;
        case "sample-complete":
          clearTimeout(timeout);
          ws.close();
          if ("onComplete" in callbacks) {
            callbacks.onComplete(msg.totalSamples);
          }
          resolve();
          break;
        case "upload-complete":
          clearTimeout(timeout);
          ws.close();
          if ("onComplete" in callbacks) {
            callbacks.onComplete(msg.totalSamples);
          }
          resolve();
          break;
        case "sample-error":
          clearTimeout(timeout);
          ws.close();
          reject(new Error(msg.error));
          break;
      }
    });
    ws.addEventListener("error", () => {
      clearTimeout(timeout);
      reject(new Error("WebSocket connection error"));
    });
  });
}
export {
  CMD_ACK,
  CMD_CANCEL,
  CMD_DATA_PACKET,
  CMD_DUMP_HEADER,
  CMD_DUMP_REQUEST,
  CMD_NAK,
  CMD_WAIT,
  DATA_PACKET_PAYLOAD_SIZE,
  DEFAULT_MAX_RETRIES,
  DEFAULT_TIMEOUT_MS,
  LOOP_FORWARD,
  LOOP_FORWARD_BACKWARD,
  LOOP_OFF,
  MAX_BIT_DEPTH,
  MAX_SAMPLE_NUMBER,
  MIN_BIT_DEPTH,
  PACKET_COUNTER_MAX,
  SYSEX_END,
  SYSEX_START,
  UNIVERSAL_NON_REAL_TIME,
  buildAck,
  buildCancel,
  buildDataPacket,
  buildDumpHeader,
  buildDumpRequest,
  buildNak,
  buildWait,
  calculateChecksum,
  calculatePacketCount,
  createRetryWrapper,
  createScsiDiskClient,
  createScsiMidiTransport,
  createSdsReceiver,
  createSdsSender,
  createWebMidiAdapter,
  decodeSampleData,
  decodeSampleWord,
  encodeSampleData,
  encodeSampleWord,
  getBrowserCompatibility,
  isSecureContext,
  isWebMidiSupported,
  openMidiPorts,
  packetsToSamples,
  parseSdsMessage,
  requestMidiAccess,
  requestSample,
  samplesToPackets,
  validateChecksum,
  withRetry,
  withRetryResult
};
//# sourceMappingURL=index.js.map